import { PublicAxiosInstance } from '@/api/axios'
import { SanitizedHtmlRenderer } from '@/components/html.render'
import Modal from '@/components/modal'
import ConfirmModal from '@/components/modal/confirm-modal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { FullPageSkeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import type { MeetingMinuteReviewHistoryStatus } from '@/enum/global.enum'
import { useDateFormat } from '@/hooks/use-date-format'
import type { IMeetingMinute } from '@/interface/meeting-minutes.interface'
import { cn } from '@/lib/utils'
import { formatFileSize, getFileIcon, getFileTypeColor } from '@/utils/file-utils'
import { getGlobalStatusColor } from '@/utils/global-status-color.utils'
import {
  meetingMinuteReviewHistoryStatusColor,
  meetingMinuteReviewHistoryStatusLabel,
} from '@/utils/meeting-minutes-label.utils'
import type { AxiosError } from 'axios'
import { Check, Eye, FileText, Home, MessageSquare, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer'
import { Navigate, useParams } from 'react-router'
import { toast } from 'sonner'

interface IMeetingMinuteFile {
  id: string
  url: string | null
  name: string
  originalName: string
  version: number
  mimeType: string
  size: number
  isLatest: boolean
}

interface MeetingMinutesData {
  meetingMinute: Omit<IMeetingMinute, 'previousReviews' | 'files'> & {
    files: IMeetingMinuteFile[]
    previousReviews: {
      id: string
      createdAt: Date
      remarks: string
      status: MeetingMinuteReviewHistoryStatus
    }[]
  }
  accessInfo: {
    accessedAt: string
    expiresAt: string
  }
}

const getAccessErrorDisplay = (statusCode: number | null, message: string | null) => {
  if (statusCode === 400)
    return {
      title: 'Meeting Minute Not Available',
      message:
        message === 'Meeting minute is not approved'
          ? 'This meeting minute has not been approved yet and cannot be viewed. Please contact your organisation administrator.'
          : (message ?? 'This meeting minute cannot be accessed at this time.'),
    }

  if (statusCode === 401)
    return {
      title: 'Invalid Link',
      message: message ?? 'This link is invalid or has expired. Please request a new link from your administrator.',
    }

  if (statusCode === 404)
    return {
      title: 'Not Found',
      message: 'The meeting minute you are looking for could not be found.',
    }

  return {
    title: 'Access Error',
    message: message ?? 'Failed to load meeting minute. Please try again later.',
  }
}

const MeetingMinuteReviewPage = () => {
  const { token } = useParams()
  const { formatDate } = useDateFormat()
  const [remarks, setRemarks] = useState('')
  const [loading, setLoading] = useState(true)
  const [openModal, setOpenModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fileLoading, setFileLoading] = useState(false)
  const [openReviewHistory, setOpenReviewHistory] = useState(false)
  const [openFileViewer, setOpenFileViewer] = useState(false)
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<IMeetingMinuteFile | null>(null)
  const [errorStatusCode, setErrorStatusCode] = useState<number | null>(null)
  const [confirmType, setConfirmType] = useState<'reject' | 'approve' | null>(null)
  const [meetingMinutesData, setMeetingMinutesData] = useState<MeetingMinutesData | null>(null)

  const isImage = selectedFile?.mimeType?.startsWith('image/')
  const isPdf = selectedFile?.mimeType === 'application/pdf'

  useEffect(() => {
    if (openFileViewer && selectedFile?.url) {
      if (isImage || isPdf) {
        setFileBlobUrl(selectedFile.url)
        setFileLoading(false)
      } else {
        loadFileAsBlob()
      }
    } else {
      if (fileBlobUrl && !fileBlobUrl.startsWith('http')) URL.revokeObjectURL(fileBlobUrl)

      setFileBlobUrl(null)
      setFileLoading(false)
    }

    return () => {
      if (fileBlobUrl && !fileBlobUrl.startsWith('http')) URL.revokeObjectURL(fileBlobUrl)
    }
  }, [openFileViewer, selectedFile])

  const loadFileAsBlob = async () => {
    if (!selectedFile?.url) return

    try {
      setFileLoading(true)
      try {
        const response = await fetch(selectedFile.url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
        })

        if (!response.ok) throw new Error('Failed to fetch file')

        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        setFileBlobUrl(blobUrl)
      } catch {
        setFileBlobUrl(selectedFile.url)
      }
    } catch {
      setFileBlobUrl(selectedFile.url)
    } finally {
      setFileLoading(false)
    }
  }

  const getFileType = (file: IMeetingMinuteFile | null): string => {
    if (!file) return ''

    const fileExtension = file.name?.split('.').pop()?.toLowerCase()
    if (fileExtension) return fileExtension

    if (file.mimeType) {
      const mimeParts = file.mimeType.split('/')
      if (mimeParts.length === 2) {
        const mimeExtension = mimeParts[1].split(';')[0].trim()
        const mimeToExt: Record<string, string> = {
          jpeg: 'jpg',
          msword: 'doc',
          'vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'vnd.ms-excel': 'xls',
          'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'vnd.ms-powerpoint': 'ppt',
          'vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
        }
        return mimeToExt[mimeExtension] || mimeExtension
      }
    }

    return ''
  }

  useEffect(() => {
    const fetchMeetingMinutesData = async () => {
      try {
        setLoading(true)

        if (token) {
          // Handle secure token access
          const response = await PublicAxiosInstance.get(`/meeting-minutes/secure/${token}`)
          setMeetingMinutesData(response.data.data)
        } else {
          setError('No meeting minute identifier provided')
        }
      } catch (err) {
        const axiosError = err as AxiosError<{ message: string }>
        setErrorStatusCode(axiosError?.response?.status ?? null)
        setError(
          axiosError?.response?.data?.message ?? (err instanceof Error ? err.message : 'Failed to load meeting minute')
        )
      } finally {
        setLoading(false)
      }
    }

    fetchMeetingMinutesData()
  }, [token])

  if (!token) return <Navigate to={'/'} />
  if (loading) return <FullPageSkeleton />
  if (error) {
    const { title, message } = getAccessErrorDisplay(errorStatusCode, error)

    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='text-red-600 text-6xl mb-4'>⚠️</div>
          <h2 className='text-2xl font-bold mb-2'>{title}</h2>
          <p className='text-gray-600 mb-4 max-w-md mx-auto'>{message}</p>
          <Button onClick={() => (window.location.href = '/')}>
            <Home className='h-4 w-4' />
            Go Home
          </Button>
        </div>
      </div>
    )
  }

  const handleRejectMeetingMinute = async (isReject: boolean = false) => {
    if (remarks.length < 0) {
      toast.error(`Please enter your  review remark `)
      return
    }
    try {
      await PublicAxiosInstance.post(`/meeting-minutes/review/${token}`, {
        remarks: remarks,
        isReject: isReject,
      })
      toast.success(`Meeting Minute ${isReject ? 'rejected' : 'approved'} successfully`)

      // again fetch the meeting minute data
      if (token) {
        const response = await PublicAxiosInstance.get(`/meeting-minutes/secure/${token}`)
        setMeetingMinutesData(response.data.data)
      } else {
        setError('No meeting minute identifier provided')
      }

      setRemarks('')
    } catch (err) {
      toast.error(
        (err as AxiosError<{ message: string }>)?.response?.data?.message ||
          `Failed to ${isReject ? 'reject' : 'approve'} meeting minute`
      )
    }
  }

  if (!meetingMinutesData) return <Navigate to={'/'} />

  const { meetingMinute, accessInfo } = meetingMinutesData
  const expiresAt = new Date(accessInfo.expiresAt)
  const isExpired = new Date() > expiresAt
  if (isExpired)
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='text-red-600 text-6xl mb-4'>⏰</div>
          <h2 className='text-2xl font-bold   mb-2'>Link Expired</h2>
          <p className='text-gray-600 mb-4'>This meetingMinute link has expired on {expiresAt.toLocaleDateString()}</p>
          <Button onClick={() => (window.location.href = '/')}>
            <Home className='h-4 w-4' />
            Go Home
          </Button>
        </div>
      </div>
    )

  return (
    <div>
      <div className='min-h-screen flex flex-col'>
        {/* Fixed Header */}
        <div className='flex-shrink-0 p-4 border-b '>
          <div className='max-w-4xl mx-auto flex flex-wrap gap-2 items-center justify-between'>
            <div>
              <h1 className='text-xl font-bold '>{meetingMinute.title}</h1>
              <div className='flex items-center gap-2 mt-1'>
                <span className='text-sm text-gray-500'>{meetingMinute.type?.name}</span>
              </div>
            </div>
          </div>
        </div>
        {/* Scrollable Content */}
        <div
          className={cn(
            'flex-1   overflow-y-auto p-4',
            meetingMinute?.previousReviews && meetingMinute?.previousReviews?.length > 0
              ? 'max-h-[calc(100vh-300px)]'
              : 'max-h-[calc(100vh-275px)]'
          )}
        >
          <div className='max-w-4xl mx-auto space-y-4'>
            <div className=' border rounded-md p-4'>
              <h2 className='text-md font-semibold mb-3 flex items-center gap-1'>Meeting Minutes Details</h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
                {meetingMinute.facilitator?.firstName ? (
                  <div className='flex  gap-x-2  gap-y-2 flex-wrap'>
                    <span className='text-gray-500'>Facilitator:</span>
                    <span>
                      {meetingMinute.facilitator?.firstName} {meetingMinute.facilitator?.lastName}
                    </span>
                  </div>
                ) : null}
                {meetingMinute.dateAndTime ? (
                  <div className='flex  gap-x-2  gap-y-2 flex-wrap'>
                    <span className='text-gray-500'>Date & Time:</span>
                    <span>{formatDate(meetingMinute.dateAndTime)}</span>
                  </div>
                ) : null}
                {meetingMinute.locationOrPlatform ? (
                  <div className='flex  gap-x-2  gap-y-2 flex-wrap'>
                    <span className='text-gray-500'>Location/Platform:</span>
                    <span>{meetingMinute.locationOrPlatform}</span>
                  </div>
                ) : null}
              </div>
            </div>
            {meetingMinute.files && meetingMinute.files.length > 0 && (
              <div className='border rounded-md p-4'>
                <h2 className='text-md font-semibold mb-3 flex items-center gap-1'>
                  <FileText className='h-4 w-4 mb-[2px]' />
                  Attached Files ({meetingMinute.files.length})
                </h2>
                <div className='space-y-2'>
                  {meetingMinute.files.map((file) => {
                    const FileIcon = getFileIcon(file.mimeType)
                    return (
                      <div
                        key={file.id}
                        className='flex items-center justify-between p-2 border rounded hover:bg-muted/20 transition-colors'
                      >
                        <div className='flex items-center gap-3'>
                          <div className='relative'>
                            <FileIcon className={`h-8 w-8 ${getFileTypeColor(file.mimeType)}`} />
                            <div className='absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center'>
                              <span className='text-primary-foreground text-[10px] font-medium'>
                                v{file.version ?? 1}
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className='font-medium text-sm'>{file.originalName || file.name}</p>
                            <div className='flex items-center space-x-1 text-[10px] text-muted-foreground'>
                              <FileText className='h-[10px] w-[10px]' />
                              <span>{formatFileSize(file.size)}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          size='sm'
                          variant='outline'
                          disabled={!file.url}
                          onClick={() => {
                            setSelectedFile(file)
                            setOpenFileViewer(true)
                          }}
                          className='flex items-center gap-1 text-xs'
                        >
                          <Eye className='h-3 w-3' />
                          View
                        </Button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            <div>
              {/* <div className='flex gap-4 justify-end mb-2 flex-wrap'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    if (meetingMinute.notes)
                      downloadHtmlAsPdf(meetingMinute.notes, meetingMinute.title || 'Meeting Minutes')
                  }}
                >
                  <Download />
                  Download as PDF
                </Button>
              </div> */}
              <SanitizedHtmlRenderer htmlContent={meetingMinute.notes} />
            </div>
          </div>
        </div>
        {/* Fixed review section  */}
        <div className='flex-shrink-0 '>
          {meetingMinute?.previousReviews && meetingMinute?.previousReviews?.length > 0 ? (
            <div className='max-w-4xl mx-auto flex flex-col gap-2 border-t border-border mt-5 pt-5'>
              <div className='space-y-5'>
                <div>
                  <Label className='text-sm text-gray-500'>Remarks</Label>
                  <Label>{meetingMinute?.previousReviews?.[0]?.remarks ?? ''}</Label>
                </div>
                <div className='flex gap-6'>
                  <div>
                    <Label className='text-sm text-gray-500'>Status</Label>
                    <Badge className={getGlobalStatusColor(meetingMinute?.previousReviews[0].status)}>
                      {meetingMinuteReviewHistoryStatusLabel(meetingMinute.previousReviews[0].status)}
                    </Badge>
                  </div>
                  <div>
                    <Label className='text-sm text-gray-500'>Date</Label>
                    <Label className='pt-2'>{formatDate(meetingMinute?.previousReviews?.[0]?.createdAt)}</Label>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='max-w-4xl mx-auto flex flex-col gap-2 border-t border-border mt-5 pt-5'>
              <Textarea
                id='remarks'
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder={'Enter your review remark (required)'}
                className={cn('h-[100px]')}
                disabled={false}
              />

              <div className='flex gap-2 justify-end'>
                <Button
                  onClick={() => {
                    setConfirmType('reject')
                    setOpenModal(true)
                  }}
                  disabled={false}
                  variant={'destructive'}
                >
                  <X className='h-4 w-4' />
                  Reject
                </Button>
                <Button
                  onClick={() => {
                    setConfirmType('approve')
                    setOpenModal(true)
                  }}
                  disabled={false}
                >
                  <Check className='h-4 w-4' />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {meetingMinute?.previousReviews && meetingMinute?.previousReviews?.length > 0 ? (
        <Modal open={openReviewHistory} setOpen={setOpenReviewHistory} title='Meeting Minutes Review History'>
          <div className='relative max-h-[60vh] space-y-4 overflow-y-auto pl-2'>
            <div className='absolute top-2 bottom-0 left-2 w-px bg-border' />
            {meetingMinute?.previousReviews?.map((review) => (
              <MeetingMinuteReviewHistory key={review.id} review={review} />
            ))}
          </div>
        </Modal>
      ) : null}

      <Modal size='xl' open={openFileViewer} setOpen={setOpenFileViewer} title={selectedFile?.name ?? ''}>
        <div className='h-[calc(80vh-100px)] overflow-hidden'>
          {fileLoading ? (
            <div className='flex items-center justify-center h-full'>
              <div className='text-center'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
                <p className='text-muted-foreground'>Loading file...</p>
              </div>
            </div>
          ) : selectedFile?.url ? (
            <>
              {isImage && (
                <div className='w-full h-full flex items-center justify-center bg-black/5 dark:bg-white/5 rounded-lg overflow-auto'>
                  <img
                    loading='lazy'
                    decoding='async'
                    src={selectedFile.url}
                    alt={selectedFile.name || 'Image'}
                    className='max-w-full max-h-full object-contain'
                  />
                </div>
              )}

              {isPdf && (
                <div className='w-full h-full'>
                  <iframe
                    src={`${selectedFile.url}#toolbar=1&navpanes=1&scrollbar=1`}
                    className='w-full h-full border rounded-lg'
                    title={selectedFile.name || 'PDF Document'}
                    style={{ minHeight: '600px' }}
                  />
                </div>
              )}

              {!isImage && !isPdf && fileBlobUrl && (
                <DocViewer
                  documents={[
                    {
                      uri: fileBlobUrl,
                      fileType: getFileType(selectedFile),
                    },
                  ]}
                  pluginRenderers={DocViewerRenderers}
                  style={{ height: '100%' }}
                  config={{
                    header: {
                      disableHeader: true,
                      disableFileName: true,
                    },
                  }}
                />
              )}
            </>
          ) : (
            <div className='flex items-center justify-center h-full'>
              <div className='text-center'>
                <p className='text-muted-foreground mb-4'>File is no longer available for preview.</p>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmModal
        open={openModal}
        setOpen={setOpenModal}
        title={confirmType === 'reject' ? 'Reject Meeting Minute' : 'Approve Meeting Minute'}
        description={
          confirmType === 'reject'
            ? 'Are you sure you want to reject this meeting minute?'
            : 'Are you sure you want to approve this meeting minute?'
        }
        buttonLabel={confirmType === 'reject' ? 'Confirm Reject' : 'Confirm Approve'}
        loadingButtonLabel={confirmType === 'reject' ? 'Rejecting...' : 'Approving...'}
        isNested={true}
        submitButtonVariant={confirmType === 'reject' ? 'destructive' : 'default'}
        cancelButtonVariant='outline'
        hideToast
        onSubmit={async () => {
          setLoading(true)
          if (confirmType === 'reject') await handleRejectMeetingMinute(true)
          else if (confirmType === 'approve') await handleRejectMeetingMinute(false)
          setLoading(false)
        }}
        loading={loading}
      />
    </div>
  )
}

export default MeetingMinuteReviewPage

const MeetingMinuteReviewHistory = ({
  review,
}: {
  review: {
    id: string
    createdAt: Date
    remarks: string
    status: MeetingMinuteReviewHistoryStatus
  }
}) => {
  const { formatDate } = useDateFormat()

  return (
    <div className='relative flex gap-3 pl-4'>
      <div className='absolute top-1.5 -left-[3px] h-2 w-2 rounded-full bg-primary ring-4 ring-background' />

      <div className='flex-1 space-y-2 rounded-lg border border-border/50 bg-muted/20 p-3'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <Badge className={meetingMinuteReviewHistoryStatusColor(review.status)}>
              {meetingMinuteReviewHistoryStatusLabel(review.status)}
            </Badge>
          </div>
          <span className='text-xs text-muted-foreground'>{formatDate(review.createdAt)}</span>
        </div>

        {review.remarks ? (
          <div className='space-y-1'>
            <p className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground'>
              <MessageSquare className='h-3 w-3' />
              Reviewer Remarks
            </p>
            <p className='rounded-md border border-border/50 bg-background/80 p-2 text-sm whitespace-pre-wrap break-words'>
              {review.remarks}
            </p>
          </div>
        ) : (
          <p className='text-xs italic text-muted-foreground'>No remarks provided.</p>
        )}
      </div>
    </div>
  )
}
